// SETTINGS V2 — tablet underline tab-bar (SETTINGS_V2_SP1 §5).
//
// Port of designer STabBar (settings-responsive.jsx lines 11-29) as a real
// component. Rendered by SettingsV2Layout in the tablet branch (768–1279),
// directly below the compact page head, edge-to-edge (full-bleed — no page
// padding on the <nav> itself).
//
// Flat group order is DERIVED from useSettingsV2Nav().groups.flatMap(...) so the
// `visible` filter seam is preserved automatically — NO separate flat constant.
// Active matching uses the shared isSettingsV2SectionActive helper (§4.2).
// Horizontal scroll + scrollbar-hide + hover/focus live in settings-v2-tokens
// .scss (.sv2-tabbar-scroll / .sv2-tab); the active color/weight is applied
// inline (wins over the class hover on specificity — the .sv2-nav-item pattern).
// Overflow story = scroll + right-edge fade ONLY (no dropdown, no overflow menu).
//
// Styling stack: inline styles + scoped CSS-var tokens + NavLink. No MUI, no
// sx, no dt, no Tailwind. No new i18n keys (aria-label + labels already exist).
import React, { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import useSettingsV2Nav, { isSettingsV2SectionActive } from "../useSettingsV2Nav";
import { SETTINGS_BASE } from "../settingsV2Paths";
import settingsV2NavGlyphs from "./settingsV2NavGlyphs";

const SettingsV2TabBar: React.FC = () => {
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const { groups } = useSettingsV2Nav();

  // Flat group order (Účet → Firma → Servis a obchod). Deriving from `groups`
  // keeps the visible-filter seam without a parallel list.
  const flat = groups.flatMap((g) => g.items);

  const activeRef = useRef<HTMLAnchorElement | null>(null);
  const activeKey = flat.find((it) =>
    isSettingsV2SectionActive(location.pathname, it.path)
  )?.key;

  // Auto-scroll the active tab into view on load / active change. Instant (no
  // smooth behavior) → no reduced-motion concern.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeKey]);

  return (
    <nav
      aria-label={translate("common:navigation.settings")}
      style={{
        position: "relative",
        background: "var(--card)",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
      }}
    >
      <div
        className="sv2-tabbar-scroll"
        style={{ display: "flex", gap: 2, padding: "0 20px" }}
      >
        {flat.map((item) => {
          const active = isSettingsV2SectionActive(location.pathname, item.path);
          const Glyph = settingsV2NavGlyphs[item.key];
          return (
            <NavLink
              key={item.key}
              to={`${SETTINGS_BASE}/${item.path}`}
              ref={active ? activeRef : undefined}
              className="sv2-tab"
              style={{
                position: "relative",
                padding: "11px 11px 12px",
                fontSize: 12.5,
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                textDecoration: "none",
                // Inactive color comes from .sv2-tab; active wins inline.
                color: active ? "var(--mod-accent)" : undefined,
                fontWeight: active ? 600 : 400,
              }}
            >
              {Glyph && <Glyph />}
              <span>{translate(item.labelKey)}</span>
              {active && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 8,
                    right: 8,
                    bottom: -1,
                    height: 2,
                    background: "var(--mod-accent)",
                  }}
                />
              )}
            </NavLink>
          );
        })}
      </div>
      {/* Right-edge fade — signals horizontal overflow (designer exact). */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 1,
          width: 28,
          background: "linear-gradient(90deg, transparent, var(--card))",
          pointerEvents: "none",
        }}
      />
    </nav>
  );
};

export default SettingsV2TabBar;
